import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Icon } from '../components/common/Icons';

// ── Defined OUTSIDE SignupPage so React never remounts it on re-render ──
function Field({ id, label, type = 'text', placeholder, required = true, value, onChange, error, hint }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label}
        {required
          ? <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>
          : <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 6, color: 'var(--text-muted)' }}>(optional)</span>
        }
      </label>
      <input
        id={id}
        className={`form-input ${error ? 'error' : ''}`}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      {error && (
        <div style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '0.3rem' }}>{error}</div>
      )}
      {hint && !error && (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.3rem' }}>{hint}</div>
      )}
    </div>
  );
}

export default function SignupPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  // Step 1: Form details, Step 2: OTP verification
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    firstName: '', lastName: '', username: '',
    email: '', password: '', phoneNumber: ''
  });

  // 6 individual OTP digit boxes
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpInputsRef  = useRef([]);

  const [loading,       setLoading]       = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer,   setResendTimer]   = useState(0);
  const [errors,        setErrors]        = useState({});

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Cooldown countdown timer for OTP resend
  useEffect(() => {
    let interval = null;
    if (step === 2 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, resendTimer]);

  // Auto-focus first digit input when entering Step 2
  useEffect(() => {
    if (step === 2 && otpInputsRef.current[0]) {
      setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
    }
  }, [step]);

  const validateStep1 = () => {
    const e = {};
    if (!form.firstName.trim())                        e.firstName = 'Required';
    if (!form.lastName.trim())                         e.lastName  = 'Required';
    if (!form.username || form.username.length < 3)    e.username  = 'Min 3 characters';
    else if (!/^[a-zA-Z0-9_.-]+$/.test(form.username)) e.username = 'Letters, digits, _ . - only';
    if (!form.email || !form.email.includes('@'))      e.email     = 'Valid email required';
    if (!form.password || form.password.length < 8)    e.password  = 'Min 8 characters';
    else if (!/[A-Z]/.test(form.password))             e.password  = 'Needs an uppercase letter';
    else if (!/[a-z]/.test(form.password))             e.password  = 'Needs a lowercase letter';
    else if (!/[0-9]/.test(form.password))             e.password  = 'Needs a digit';
    else if (!/[^A-Za-z0-9]/.test(form.password))      e.password  = 'Needs a special character (!@#$ etc.)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Submit Step 1: Validate and send OTP
  const handleProceedToOtp = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;

    setLoading(true);
    setErrors({});

    try {
      await authApi.sendOtp({
        email:    form.email.trim(),
        username: form.username.trim(),
      });
      setStep(2);
      setResendTimer(60);
      setOtp(['', '', '', '', '', '']);
      toast.info(`Verification code sent to ${form.email.trim()}`);
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to send verification code';
      setErrors({ general: msg });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0 || resendLoading) return;
    setResendLoading(true);
    setErrors({});

    try {
      await authApi.sendOtp({
        email:    form.email.trim(),
        username: form.username.trim(),
      });
      setResendTimer(60);
      setOtp(['', '', '', '', '', '']);
      if (otpInputsRef.current[0]) otpInputsRef.current[0].focus();
      toast.success(`New verification code sent to ${form.email.trim()}`);
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to resend verification code';
      setErrors({ otp: msg });
    } finally {
      setResendLoading(false);
    }
  };

  // Handle individual OTP digit change
  const handleDigitChange = (index, value) => {
    // Only accept numbers
    const cleanVal = value.replace(/[^0-9]/g, '');

    if (cleanVal.length === 0) {
      const nextOtp = [...otp];
      nextOtp[index] = '';
      setOtp(nextOtp);
      return;
    }

    // Single digit entry
    const char = cleanVal.slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = char;
    setOtp(nextOtp);

    // Auto-advance focus to next digit box
    if (index < 5 && otpInputsRef.current[index + 1]) {
      otpInputsRef.current[index + 1].focus();
    }
  };

  // Handle Backspace and arrow keys
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0 && otpInputsRef.current[index - 1]) {
        otpInputsRef.current[index - 1].focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  // Support pasting full 6-digit code
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digitsOnly = pastedData.replace(/[^0-9]/g, '').slice(0, 6);

    if (digitsOnly.length > 0) {
      const nextOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        nextOtp[i] = digitsOnly[i] || '';
      }
      setOtp(nextOtp);

      const focusIdx = Math.min(digitsOnly.length, 5);
      otpInputsRef.current[focusIdx]?.focus();
    }
  };

  // Submit Step 2: Verify OTP and create account
  const handleVerifyAndSignup = async (e) => {
    e.preventDefault();
    const fullOtp = otp.join('');
    if (fullOtp.length < 6) {
      setErrors({ otp: 'Please enter all 6 digits of the verification code' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payload = {
        firstName:   form.firstName.trim(),
        lastName:    form.lastName.trim(),
        username:    form.username.trim(),
        email:       form.email.trim(),
        password:    form.password,
        phoneNumber: form.phoneNumber.trim() || undefined,
        otp:         fullOtp,
      };

      const { data } = await authApi.signup(payload);
      login(data.user, data.accessToken);
      navigate('/');
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Verification failed. Please check the code and try again.';
      setErrors({ otp: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-glow-orb" />
      <div className="auth-card" style={{ maxWidth: 480 }}>
        {/* Logo */}
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <Icon name="note" size={22} />
          </div>
          <span className="auth-logo-text">Memora</span>
        </div>

        {/* Stepper Progress */}
        <div className="auth-stepper">
          <div className={`stepper-step ${step === 1 ? 'active' : 'completed'}`}>
            <div className="stepper-circle">
              {step > 1 ? <Icon name="check" size={13} /> : '1'}
            </div>
            <span>Account Details</span>
          </div>

          <div className={`stepper-divider ${step >= 2 ? 'active' : ''}`} />

          <div className={`stepper-step ${step === 2 ? 'active' : ''}`}>
            <div className="stepper-circle">2</div>
            <span>Verification</span>
          </div>
        </div>

        {/* Step 1: Account Information */}
        {step === 1 && (
          <>
            <h1 className="auth-title">Create an account</h1>
            <p className="auth-subtitle">Enter your details to get started</p>

            {errors.general && <div className="alert-custom error">{errors.general}</div>}

            <form onSubmit={handleProceedToOtp}>
              <div className="row g-3" style={{ marginBottom: 0 }}>
                <div className="col-6">
                  <Field
                    id="firstName" label="First Name" placeholder="Zaheen"
                    value={form.firstName} onChange={set('firstName')} error={errors.firstName}
                  />
                </div>
                <div className="col-6">
                  <Field
                    id="lastName" label="Last Name" placeholder="Khan"
                    value={form.lastName} onChange={set('lastName')} error={errors.lastName}
                  />
                </div>
              </div>

              <Field
                id="username" label="Username" placeholder="e.g. zaheen_k"
                value={form.username} onChange={set('username')} error={errors.username}
              />
              <Field
                id="email" label="Email" type="email" placeholder="you@example.com"
                value={form.email} onChange={set('email')} error={errors.email}
              />
              <Field
                id="password" label="Password" type="password"
                placeholder="Min 8 chars, upper, lower, digit, special"
                value={form.password} onChange={set('password')} error={errors.password}
                hint="Must contain: uppercase · lowercase · digit · special character"
              />
              <Field
                id="phoneNumber" label="Phone Number" type="tel"
                placeholder="+91 98765 43210" required={false}
                value={form.phoneNumber} onChange={set('phoneNumber')} error={errors.phoneNumber}
              />

              <button
                className="btn-primary-custom"
                type="submit"
                disabled={loading}
                style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
              >
                {loading ? (
                  <><span className="spinner" /> Sending verification code…</>
                ) : (
                  'Continue to Verification →'
                )}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Already have an account? <Link to="/login" className="auth-link">Sign in</Link>
            </p>
          </>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <>
            <h1 className="auth-title">Verify your email</h1>
            <p className="auth-subtitle" style={{ marginBottom: '1.25rem' }}>
              We sent a 6-digit verification code to
            </p>

            <div className="otp-info-box">
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                {form.email}
              </div>
              <button
                type="button"
                className="btn-back-link"
                onClick={() => {
                  setStep(1);
                  setErrors({});
                }}
              >
                ← Edit details or change email
              </button>
            </div>

            {errors.otp && <div className="alert-custom error">{errors.otp}</div>}

            <form onSubmit={handleVerifyAndSignup}>
              <div className="otp-inputs-wrapper" onPaste={handlePaste}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={el => (otpInputsRef.current[idx] = el)}
                    className={`otp-digit-input ${digit ? 'filled' : ''} ${errors.otp ? 'error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    aria-label={`Digit ${idx + 1}`}
                  />
                ))}
              </div>

              <div className="otp-resend-row">
                <span>Didn't receive the code?</span>
                <button
                  type="button"
                  className="otp-resend-btn"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || resendLoading}
                >
                  {resendLoading
                    ? 'Sending…'
                    : resendTimer > 0
                    ? `Resend in ${resendTimer}s`
                    : 'Resend Code'}
                </button>
              </div>

              <button
                className="btn-primary-custom"
                type="submit"
                disabled={loading || otp.join('').length < 6}
                style={{ width: '100%', justifyContent: 'center', marginTop: '1.5rem' }}
              >
                {loading ? (
                  <><span className="spinner" /> Verifying & creating account…</>
                ) : (
                  'Verify & Create Account'
                )}
              </button>

              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  type="button"
                  className="btn-back-link"
                  onClick={() => {
                    setStep(1);
                    setErrors({});
                  }}
                >
                  ← Back to Step 1
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
